const parseLocation = (location) => {
  const coordinates = Array.isArray(location?.loc)
    ? location.loc
    : location?.loc?.split(',');

  if (coordinates?.length === 2) {
    return {
      source_lat: coordinates[0],
      source_lon: coordinates[1]
    };
  }

  const sourceLat = location?.source_lat ?? location?.lat ?? location?.latitude;
  const sourceLon = location?.source_lon ?? location?.lon ?? location?.lng ?? location?.longitude;

  return {
    ...(sourceLat != null && { source_lat: sourceLat }),
    ...(sourceLon != null && { source_lon: sourceLon })
  };
};

export const useChatbot = async (prompt, sessionId, location, options = {}) => {
  const config = useRuntimeConfig();
  const tokens = useTokens();
  const endpoint = `${config.public.apiUrl?.replace(/\/$/, '') || ''}/api/chatbot/chat/`;
  const body = {
    prompt,
    ...(sessionId && { thread_id: sessionId }),
    ...parseLocation(location),
    user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    user_utc_offset_minutes: -new Date().getTimezoneOffset()
  };

  const request = (accessToken) => fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` })
    },
    body: JSON.stringify(body),
    signal: options.signal
  });

  let response = await request(tokens.access);

  if (response.status === 401 && tokens.refresh) {
    const accessToken = await useRefreshAccessToken(tokens.refresh);
    tokens.setAccessToken(accessToken);
    useUser().setTokens(accessToken, tokens.refresh);
    response = await request(accessToken);
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.detail || `Chatbot request failed with status ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Chatbot response stream is unavailable');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';
  let messageId = null;
  let responseSessionId = response.headers.get('X-Chatbot-Thread-Id') || sessionId;

  const processEvent = (event) => {
    const payload = event
      .split(/\r?\n/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n');

    if (!payload || payload === '[DONE]') return;

    const data = JSON.parse(payload);
    if (data.error) throw new Error(data.error);

    responseSessionId = data.thread_id || responseSessionId;
    messageId = data.message_id || data.message?.id || messageId;

    const delta = data.delta || (!data.done && typeof data.message === 'string' ? data.message : '');
    if (delta) {
      content += delta;
      options.onDelta?.(delta, content);
    } else if (!content && typeof data.message?.content === 'string') {
      content = data.message.content;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const events = buffer.split(/\r?\n\r?\n/);
    buffer = done ? '' : events.pop();
    events.forEach(processEvent);

    if (done) {
      if (buffer.trim()) processEvent(buffer);
      break;
    }
  }

  return {
    sessionId: responseSessionId,
    message: messageId ? { id: messageId, content } : content
  };
};
